/**
 * @fileoverview Componente responsável por editar mensagens já cadastradas ou enviadas.
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  originalMessage: {
    backgroundColor: theme.palette.type === 'light' ? "#f5f5f5" : "#2a2a2a",
    padding: theme.spacing(1.5),
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  originalLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginBottom: 4,
  },
  originalText: {
    fontSize: 14,
    color: theme.palette.text.primary,
    wordBreak: "break-word",
  },
  textField: {
    marginTop: theme.spacing(1),
  },
  timeInfo: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  timeRemaining: {
    fontSize: 12,
  },
  charCounter: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
}));

// Limite de edição em minutos
const EDIT_TIME_LIMIT_MINUTES = 10;

const EditMessageModal = ({ open, onClose, message }) => {
  const classes = useStyles();
  const [newBody, setNewBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    if (open && message) {
      setNewBody(message.body || "");
      calculateTimeRemaining();
    }
  }, [open, message]);

  // Timer para atualizar tempo restante a cada segundo
  useEffect(() => {
    if (!open) return;

    const interval = setInterval(() => {
      calculateTimeRemaining();
    }, 1000);

    return () => clearInterval(interval);
  }, [open, message]);

  const calculateTimeRemaining = () => {
    if (!message?.createdAt) return;

    const messageDate = new Date(message.createdAt);
    const now = new Date();
    const diffMs = (EDIT_TIME_LIMIT_MINUTES * 60 * 1000) - (now.getTime() - messageDate.getTime());

    if (diffMs <= 0) {
      setTimeRemaining(0);
    } else {
      const minutes = Math.floor(diffMs / 60000);
      const seconds = Math.floor((diffMs % 60000) / 1000);
      setTimeRemaining({ minutes, seconds });
    }
  };

  const formatTimeRemaining = () => {
    if (!timeRemaining) return "";
    if (timeRemaining === 0) return i18n.t("editMessageModal.timeExpired");
    return `${timeRemaining.minutes}:${String(timeRemaining.seconds).padStart(2, '0')}`;
  };

  const handleSubmit = async () => {
    if (!newBody.trim()) {
      toast.error(i18n.t("editMessageModal.errors.emptyMessage"));
      return;
    }

    if (newBody.trim() === message.body) {
      toast.info(i18n.t("editMessageModal.errors.noChanges"));
      onClose();
      return;
    }

    if (timeRemaining === 0) {
      toast.error(i18n.t("editMessageModal.errors.timeExpired"));
      onClose();
      return;
    }

    setLoading(true);
    try {
      await api.put(`/messages/${message.id}/edit`, {
        newBody: newBody.trim()
      });

      toast.success(i18n.t("editMessageModal.success"));
      onClose();
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleClose = () => {
    if (!loading) {
      setNewBody("");
      onClose();
    }
  };

  const isTimeExpired = timeRemaining === 0;
  const hasChanges = newBody.trim() !== message?.body;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: { borderRadius: 12 }
      }}
    >
      <DialogTitle>
        {i18n.t("editMessageModal.title")}
      </DialogTitle>

      <DialogContent>
        {/* Mensagem original */}
        <Box className={classes.originalMessage}>
          <Typography className={classes.originalLabel}>
            {i18n.t("editMessageModal.originalMessage")}
          </Typography>
          <Typography className={classes.originalText}>
            {message?.body}
          </Typography>
        </Box>

        {/* Campo de edição */}
        <TextField
          className={classes.textField}
          label={i18n.t("editMessageModal.newMessage")}
          multiline
          rows={4}
          variant="outlined"
          fullWidth
          value={newBody}
          onChange={(e) => setNewBody(e.target.value)}
          disabled={loading || isTimeExpired}
          autoFocus
        />

        {/* Info de tempo e caracteres */}
        <Box className={classes.timeInfo}>
          <Typography
            className={classes.timeRemaining}
            style={{ color: isTimeExpired ? "#f44336" : "#ff9800" }}
          >
            {isTimeExpired
              ? i18n.t("editMessageModal.timeExpired")
              : `${i18n.t("editMessageModal.timeRemaining")}: ${formatTimeRemaining()}`
            }
          </Typography>
          <Typography className={classes.charCounter}>
            {newBody.length} {i18n.t("editMessageModal.characters")}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: 16 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          color="default"
        >
          {i18n.t("editMessageModal.buttons.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || isTimeExpired || !hasChanges || !newBody.trim()}
          color="primary"
          variant="contained"
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            i18n.t("editMessageModal.buttons.save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditMessageModal;
